import { REGEX_EMAIL } from "@src/config/constant";
import { formatNPWP } from "@src/config/helper";
import { DOB_MAX_AGE } from "@src/config/id/constant";

const REGISTER_UPLOAD_TYPES = ["file", "image"];
const REGISTER_SELECT_TYPES = ["dd", "sdd", "state", "city", "locality", "all_city"];
const REGISTER_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
const REGISTER_ALPHA_REGEX = /^[A-Za-z\s'.-]+$/;
const REGISTER_ALPHANUMERIC_REGEX = /^[A-Za-z0-9\s./&()'-]+$/;
export const REGISTER_DOCUMENT_UPLOAD_CATEGORY = "document";
export const REGISTER_BANK_UPLOAD_CATEGORY = "bank_statement";
const REGISTER_NUMBER_FIELDS = [
    "marital_status",
    "gender",
    "city_birth",
    "province",
    "city",
    "district",
    "bank_id",
] as const;
const REGISTER_FIELD_RULES: Record<string, Record<string, any>> = {
    name: {
        sanitize: "alphabetic",
        regex: REGISTER_ALPHA_REGEX,
    },
    referral: {
        sanitize: "alphanumeric",
        regex: REGISTER_ALPHANUMERIC_REGEX,
    },
    ktp_id: {
        digitsOnly: true,
        exactLength: 16,
    },
    npwp_no: {
        sanitize: "npwp",
        exactLength: 20,
    },
    housing: {
        sanitize: "alphanumeric",
        regex: REGISTER_ALPHANUMERIC_REGEX,
    },
    rt: {
        digitsOnly: true,
        exactLength: 3,
    },
    rw: {
        digitsOnly: true,
        exactLength: 3,
    },
    sub_district: {
        sanitize: "alphabetic",
        regex: REGISTER_ALPHA_REGEX,
    },
    account_no: {
        digitsOnly: true,
    },
    beneficiary_name: {
        sanitize: "alphanumeric",
        regex: REGISTER_ALPHANUMERIC_REGEX,
    },
};

export const getFieldKey = (fieldItem: any, fallbackIndex = 0) => {
    return fieldItem?.name || fieldItem?.key || `field_${fieldItem?.id ?? fallbackIndex}`;
};

export const getFieldDisplayLabel = (fieldItem: any) => {
    let label = fieldItem?.label || "";

    if (!label) {
        return "";
    }

    // Only fix exact "Email Id" (case-insensitive)
    if (/^email id$/i.test(label.trim())) {
        label = "Email ID";
    }

    // Add format for DOB
    if (/^dob$/i.test(label.trim())) {
        label = "DOB (DD/MM/YY)";
    }

    if (fieldItem?.type !== "label" && fieldItem?.is_required === false && !/optional/i.test(label)) {
        return `${label} (Optional)`;
    }

    return label;
};

export const getUploadFileName = (fileItem: any) => {
    const rawFile = fileItem?.file instanceof File ? fileItem.file : fileItem;

    if (rawFile instanceof File) {
        return rawFile.name;
    }

    if (typeof fileItem === "string") {
        return fileItem.split("/").pop() || fileItem;
    }

    return fileItem?.name || fileItem?.src?.split("/")?.pop() || "Uploaded file";
};

const getUploadSource = (fileItem: any) => {
    if (typeof fileItem === "string") {
        return fileItem;
    }

    if (fileItem?.preview_url) {
        return fileItem.preview_url;
    }

    if (fileItem?.src) {
        return fileItem.src;
    }

    if (fileItem?.file_url) {
        return fileItem.file_url;
    }

    return "";
};

export const isUploadImageFile = (fileItem: any) => {
    const rawFile = fileItem?.file instanceof File ? fileItem.file : fileItem;

    if (rawFile instanceof File) {
        return rawFile.type.startsWith("image/");
    }

    if (typeof fileItem?.type === "string" && fileItem.type.toLowerCase().startsWith("image/")) {
        return true;
    }

    const fileNameSource =
        getUploadFileName(fileItem) ||
        fileItem?.file_name ||
        fileItem?.name ||
        fileItem?.src ||
        fileItem?.file_url ||
        getUploadSource(fileItem);
    const fileName = `${fileNameSource || ""}`.split("?")[0];
    const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

    return REGISTER_IMAGE_EXTENSIONS.includes(fileExtension);
};

export const getUploadPreviewSrc = (fileItem: any) => {
    return isUploadImageFile(fileItem) ? getUploadSource(fileItem) : "";
};

export const createRegisterUploadItem = (fileItem: File) => {
    return {
        file: fileItem,
        name: fileItem.name,
        type: fileItem.type,
        preview_url: fileItem.type.startsWith("image/") ? URL.createObjectURL(fileItem) : "",
    };
};

export const getRegisterUploadFile = (fileItem: any): File | null => {
    if (fileItem instanceof File) {
        return fileItem;
    }

    if (fileItem?.file instanceof File) {
        return fileItem.file;
    }

    return null;
};

export const createUploadedRegisterUploadItem = (
    fileItem: any,
    uploadedFile: Record<string, any>,
    metadata: Record<string, any> = {}
) => {
    const rawFile = getRegisterUploadFile(fileItem);

    return {
        name: rawFile?.name || fileItem?.name || uploadedFile?.file_name?.split("/")?.pop() || "Uploaded file",
        type: rawFile?.type || fileItem?.type || "",
        preview_url: fileItem?.preview_url || "",
        src: uploadedFile?.file_url || fileItem?.src || "",
        file_url: uploadedFile?.file_url || fileItem?.file_url || "",
        file_name: uploadedFile?.file_name || fileItem?.file_name || "",
        file_size: uploadedFile?.file_size || fileItem?.file_size || 0,
        ...metadata,
    };
};

export const revokeRegisterUploadPreview = (fileItem: any) => {
    const previewUrl = fileItem?.preview_url;

    if (typeof previewUrl === "string" && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
    }
};

export const getComparableFieldValue = (value: any) => {
    if (value && typeof value === "object" && "value" in value) {
        return value.value;
    }

    return value;
};

export const hasFieldValue = (fieldItem: any) => {
    if (!fieldItem) {
        return false;
    }

    if (REGISTER_UPLOAD_TYPES.includes(fieldItem?.type)) {
        return (fieldItem?.uploaded_files || []).length > 0;
    }

    const rawValue = getComparableFieldValue(fieldItem?.value);

    if (Array.isArray(rawValue)) {
        return rawValue.length > 0;
    }

    if (typeof rawValue === "string") {
        return rawValue.trim() !== "";
    }

    return rawValue !== "" && rawValue !== null && rawValue !== undefined;
};

export const normalizeOptions = (options: any[] = []) => {
    return (options || []).map((option: any) => {
        if (option?.label !== undefined && option?.value !== undefined) {
            return option;
        }

        if (option?.id !== undefined && option?.value !== undefined && option?.label === undefined) {
            return {
                ...option,
                label: option.value,
                value: option.id,
            };
        }

        return {
            ...option,
            label: option?.label ?? option?.value ?? option?.name ?? "",
            value: option?.value ?? option?.id ?? option?.name ?? option?.label ?? "",
        };
    });
};

export const flattenSectionFields = (sectionItem: any) => {
    return (sectionItem?.blocks || []).flatMap((blockItem: any) => blockItem?.fields || []);
};

export const getRegisterUploadCategory = (sectionItem: any) => {
    if (sectionItem?.key === "documents") {
        return REGISTER_DOCUMENT_UPLOAD_CATEGORY;
    }

    if (sectionItem?.key === "bank") {
        return REGISTER_BANK_UPLOAD_CATEGORY;
    }

    return "";
};

export const hasPendingRegisterUploads = (sectionItem: any) => {
    return flattenSectionFields(sectionItem).some((fieldItem: any) => {
        if (!REGISTER_UPLOAD_TYPES.includes(fieldItem?.type)) {
            return false;
        }

        return (fieldItem?.uploaded_files || []).some((uploadedItem: any) => Boolean(getRegisterUploadFile(uploadedItem)));
    });
};

export const syncRegisterUploadMetadata = (sectionItem: any) => {
    const uploadCategory = getRegisterUploadCategory(sectionItem);

    if (!uploadCategory) {
        return sectionItem;
    }

    const nextSection = structuredClone(sectionItem);

    flattenSectionFields(nextSection).forEach((fieldItem: any) => {
        if (!REGISTER_UPLOAD_TYPES.includes(fieldItem?.type) || !(fieldItem?.uploaded_files || []).length) {
            return;
        }

        fieldItem.uploaded_files = (fieldItem?.uploaded_files || []).map((uploadedItem: any) => ({
            ...uploadedItem,
            upload_category: uploadedItem?.upload_category || uploadCategory,
            ...(uploadCategory === REGISTER_DOCUMENT_UPLOAD_CATEGORY && uploadedItem?.document_id === undefined
                ? { document_id: fieldItem?.id }
                : {}),
        }));
    });

    return nextSection;
};

const getRegisterFieldRule = (fieldItem: any) => {
    return REGISTER_FIELD_RULES[fieldItem?.name || ""] || {};
};

export const sanitizeRegisterTextValue = (fieldItem: any, fieldValue: string) => {
    const fieldRule = getRegisterFieldRule(fieldItem);
    let nextValue = fieldValue;

    if (fieldRule?.sanitize === "npwp") {
        nextValue = formatNPWP(nextValue);
    } else if (fieldRule?.digitsOnly || fieldItem?.type === "mobile" || fieldItem?.keyboard_type === "number-pad") {
        nextValue = nextValue.replace(/\D/g, "");
    } else if (fieldRule?.sanitize === "alphabetic") {
        nextValue = nextValue.replace(/[^A-Za-z\s'.-]/g, "");
    } else if (fieldRule?.sanitize === "alphanumeric") {
        nextValue = nextValue.replace(/[^A-Za-z0-9\s./&()'-]/g, "");
    }

    return nextValue;
};

const sanitizeField = (fieldItem: any) => {
    const nextField = { ...fieldItem };

    if (REGISTER_UPLOAD_TYPES.includes(nextField?.type)) {
        nextField.uploaded_files = Array.isArray(nextField?.uploaded_files) ? nextField.uploaded_files : [];
    } else if (nextField?.value === undefined || nextField?.value === null) {
        nextField.value = "";
    }

    nextField.error_message = nextField?.error_message || "";

    return nextField;
};

export const prepareRegisterSections = (sections: any[] = []) => {
    return structuredClone(sections || []).map((sectionItem: any) => ({
        ...sectionItem,
        blocks: (sectionItem?.blocks || []).map((blockItem: any) => ({
            ...blockItem,
            fields: (blockItem?.fields || []).map((fieldItem: any) => sanitizeField(fieldItem)),
        })),
    }));
};

const applyTextValidation = (fieldItem: any, fieldValue: string) => {
    const minLength = fieldItem?.validation?.min_length;
    const maxLength = fieldItem?.validation?.max_length;
    const validationRegex = fieldItem?.validation?.regex;
    const fieldRule = getRegisterFieldRule(fieldItem);
    const getFieldErrorMessage = () => fieldItem?.error_msg || `Please enter a valid ${fieldItem?.label?.toLowerCase()}.`;

    if (fieldRule?.sanitize === "npwp") {
        return fieldValue.length === 20 ? "" : getFieldErrorMessage();
    }

    if (fieldRule?.regex && !fieldRule.regex.test(fieldValue)) {
        return getFieldErrorMessage();
    }

    if (fieldRule?.exactLength && fieldValue.length !== fieldRule.exactLength) {
        return getFieldErrorMessage();
    }

    if (minLength && fieldValue.length < minLength) {
        return getFieldErrorMessage();
    }

    if (maxLength && fieldValue.length > maxLength) {
        return getFieldErrorMessage();
    }

    if (validationRegex) {
        const regex = new RegExp(validationRegex);

        if (!regex.test(fieldValue)) {
            return getFieldErrorMessage();
        }
    }

    return "";
};

const getFieldError = (fieldItem: any) => {
    if (!fieldItem || fieldItem?.is_show === false || fieldItem?.type === "label") {
        return "";
    }

    if (REGISTER_UPLOAD_TYPES.includes(fieldItem?.type)) {
        const uploadedFiles = fieldItem?.uploaded_files || [];
        const minimumUpload = fieldItem?.min_upload ?? (fieldItem?.is_required ? 1 : 0);
        const maximumUpload = fieldItem?.max_upload ?? uploadedFiles.length;

        if (fieldItem?.is_required && uploadedFiles.length < minimumUpload) {
            return fieldItem?.error_msg || `Please upload ${fieldItem?.label?.toLowerCase()}.`;
        }

        if (uploadedFiles.length > maximumUpload) {
            return fieldItem?.error_msg || `Please upload valid files for ${fieldItem?.label?.toLowerCase()}.`;
        }

        return "";
    }

    const comparableValue = getComparableFieldValue(fieldItem?.value);
    const textValue = comparableValue === null || comparableValue === undefined ? "" : String(comparableValue).trim();
    const isEmpty = !hasFieldValue(fieldItem);

    if (isEmpty) {
        return fieldItem?.is_required ? fieldItem?.error_msg || `Please enter ${fieldItem?.label?.toLowerCase()}.` : "";
    }

    if (fieldItem?.type === "mobile") {
        const digits = textValue.replace(/\D/g, "");
        const minLength = fieldItem?.validation?.min_length ?? 8;
        const maxLength = fieldItem?.validation?.max_length ?? 13;

        if (!digits.startsWith("8") || digits.length < minLength || digits.length > maxLength) {
            return fieldItem?.error_msg || "Please enter the valid mobile number.";
        }

        return "";
    }

    if (fieldItem?.name === "dob") {
        const selectedDate = new Date(fieldItem?.value);
        const minimumAdultDate = new Date();
        minimumAdultDate.setFullYear(minimumAdultDate.getFullYear() - DOB_MAX_AGE);

        if (Number.isNaN(selectedDate.getTime()) || selectedDate > minimumAdultDate) {
            return "Minimum age should be 18 years.";
        }

        return "";
    }

    if (fieldItem?.name === "email" || fieldItem?.type === "email" || fieldItem?.keyboard_type === "email-address") {
        if (!REGEX_EMAIL.test(textValue)) {
            return fieldItem?.error_msg || "Please enter the valid Email ID.";
        }
    }

    if (fieldItem?.name !== "npwp_no" && fieldItem?.keyboard_type === "number-pad" && !/^\d+$/.test(textValue)) {
        return fieldItem?.error_msg || `Please enter a valid ${fieldItem?.label?.toLowerCase()}.`;
    }

    if (fieldItem?.type === "radio") {
        return textValue ? "" : fieldItem?.error_msg || `Please select ${fieldItem?.label?.toLowerCase()}.`;
    }

    if (REGISTER_SELECT_TYPES.includes(fieldItem?.type) || fieldItem?.type === "date") {
        return textValue ? "" : fieldItem?.error_msg || `Please select ${fieldItem?.label?.toLowerCase()}.`;
    }

    return applyTextValidation(fieldItem, textValue);
};

export const validateRegisterSection = (sectionItem: any) => {
    const nextSection = structuredClone(sectionItem);
    let isValid = true;

    nextSection?.blocks?.forEach((blockItem: any) => {
        blockItem?.fields?.forEach((fieldItem: any) => {
            const errorMessage = getFieldError(fieldItem);
            fieldItem.error_message = errorMessage;

            if (errorMessage) {
                isValid = false;
            }
        });
    });

    return { isValid, sectionItem: nextSection };
};

const getRegisterFieldOutputValue = (fieldItem: any) => {
    if (REGISTER_UPLOAD_TYPES.includes(fieldItem?.type)) {
        return fieldItem?.uploaded_files || [];
    }

    if (fieldItem?.type === "date" && fieldItem?.value instanceof Date) {
        const year = fieldItem.value.getFullYear();
        const month = `${fieldItem.value.getMonth() + 1}`.padStart(2, "0");
        const date = `${fieldItem.value.getDate()}`.padStart(2, "0");

        return `${year}-${month}-${date}`;
    }

    return getComparableFieldValue(fieldItem?.value);
};

const getTrimmedRegisterValue = (value: any) => {
    if (typeof value !== "string") {
        return value;
    }

    const trimmedValue = value.trim();
    return trimmedValue === "" ? undefined : trimmedValue;
};

const getNumericRegisterValue = (value: any) => {
    const normalizedValue = getTrimmedRegisterValue(value);

    if (normalizedValue === undefined || normalizedValue === null || normalizedValue === "") {
        return undefined;
    }

    const numericValue = Number(normalizedValue);
    return Number.isNaN(numericValue) ? undefined : numericValue;
};

const getNormalizedNpwp = (value: any) => {
    const normalizedValue = getTrimmedRegisterValue(value);

    if (typeof normalizedValue !== "string") {
        return normalizedValue;
    }

    return formatNPWP(normalizedValue);
};

const compactRegisterPayload = (payload: Record<string, any>) => {
    return Object.entries(payload).reduce((nextPayload: Record<string, any>, [payloadKey, payloadValue]) => {
        if (payloadValue === undefined || payloadValue === null || payloadValue === "") {
            return nextPayload;
        }

        nextPayload[payloadKey] = payloadValue;
        return nextPayload;
    }, {});
};

const getRegisterUploadedFileUrl = (fileItem: any) => {
    if (typeof fileItem === "string") {
        return fileItem;
    }

    if (typeof fileItem?.file_url === "string" && fileItem.file_url.trim()) {
        return fileItem.file_url.trim();
    }

    if (typeof fileItem?.src === "string" && fileItem.src.trim()) {
        return fileItem.src.trim();
    }

    return "";
};

const getFlattenedRegisterUploads = (registerValues: Record<string, any> = {}) => {
    return Object.values(registerValues).reduce((uploadItems: any[], fieldValue: any) => {
        if (!Array.isArray(fieldValue)) {
            return uploadItems;
        }

        return [...uploadItems, ...fieldValue];
    }, []);
};

const getRegisterUploadedDocuments = (registerValues: Record<string, any> = {}) => {
    return getFlattenedRegisterUploads(registerValues).reduce((documents: any[], fileItem: any) => {
        const fileUrl = getRegisterUploadedFileUrl(fileItem);
        const rawDocumentId = fileItem?.document_id;
        const normalizedDocumentId = Number(rawDocumentId);

        if (fileItem?.upload_category !== REGISTER_DOCUMENT_UPLOAD_CATEGORY || !fileUrl || rawDocumentId === undefined || rawDocumentId === null) {
            return documents;
        }

        documents.push({
            doc_id: Number.isNaN(normalizedDocumentId) ? rawDocumentId : normalizedDocumentId,
            doc_path: fileUrl,
        });

        return documents;
    }, []);
};

const getRegisterUploadedBankStatements = (registerValues: Record<string, any> = {}) => {
    return getFlattenedRegisterUploads(registerValues).reduce((bankStatementUrls: string[], fileItem: any) => {
        const fileUrl = getRegisterUploadedFileUrl(fileItem);

        if (fileItem?.upload_category !== REGISTER_BANK_UPLOAD_CATEGORY || !fileUrl) {
            return bankStatementUrls;
        }

        bankStatementUrls.push(fileUrl);
        return bankStatementUrls;
    }, []);
};

export const getRegisterFormValues = (sections: any[] = []) => {
    return (sections || []).reduce((formValues: Record<string, any>, sectionItem: any) => {
        flattenSectionFields(sectionItem).forEach((fieldItem: any, fieldIndex: number) => {
            const fieldKey = getFieldKey(fieldItem, fieldIndex);

            if (!fieldKey || fieldItem?.type === "label") {
                return;
            }

            formValues[fieldKey] = getRegisterFieldOutputValue(fieldItem);
        });

        return formValues;
    }, {});
};

export const getRegisterPrimaryContact = (sectionsOrValues: any[] | Record<string, any> = []) => {
    const registerValues = Array.isArray(sectionsOrValues)
        ? getRegisterFormValues(sectionsOrValues)
        : sectionsOrValues || {};

    return {
        name: registerValues?.name || "",
        email: registerValues?.email || "",
        mobile: registerValues?.mobile || "",
    };
};

export const buildRegisterDuplicateCheckPayloads = (registerValues: Record<string, any> = {}) => {
    const identityPayload = compactRegisterPayload({
        ktp_id: getTrimmedRegisterValue(registerValues?.ktp_id),
        npwp_no: getNormalizedNpwp(registerValues?.npwp_no),
    });
    const contactPayload = compactRegisterPayload({
        mobile: getTrimmedRegisterValue(registerValues?.mobile),
        email: getTrimmedRegisterValue(registerValues?.email),
        referral: getTrimmedRegisterValue(registerValues?.referral),
    });

    return [identityPayload, contactPayload].filter((payloadItem) => Object.keys(payloadItem).length > 0);
};

export const buildRegisterSectionDuplicateCheckPayloads = (sectionItem: any, registerValues: Record<string, any> = {}) => {
    const sectionFieldNames = flattenSectionFields(sectionItem)
        .map((fieldItem: any) => fieldItem?.name)
        .filter(Boolean);
    const shouldCheckIdentity = ["ktp_id", "npwp_no"].some((fieldName) => sectionFieldNames.includes(fieldName));
    const shouldCheckContact = ["mobile", "email", "referral"].some((fieldName) => sectionFieldNames.includes(fieldName));
    const payloads: Record<string, any>[] = [];

    if (shouldCheckIdentity) {
        const identityPayload = compactRegisterPayload({
            ktp_id: getTrimmedRegisterValue(registerValues?.ktp_id),
            npwp_no: getNormalizedNpwp(registerValues?.npwp_no),
        });

        if (Object.keys(identityPayload).length > 0) {
            payloads.push(identityPayload);
        }
    }

    if (shouldCheckContact) {
        const contactPayload = compactRegisterPayload({
            mobile: getTrimmedRegisterValue(registerValues?.mobile),
            email: getTrimmedRegisterValue(registerValues?.email),
            referral: getTrimmedRegisterValue(registerValues?.referral),
        });

        if (Object.keys(contactPayload).length > 0) {
            payloads.push(contactPayload);
        }
    }

    return payloads;
};

export const buildRegisterOtpPayload = (registerValues: Record<string, any> = {}) => {
    return compactRegisterPayload({
        email: getTrimmedRegisterValue(registerValues?.email),
    });
};

export const buildRegisterPayload = (registerValues: Record<string, any> = {}, otp: string) => {
    const numericFields = REGISTER_NUMBER_FIELDS.reduce((numericPayload, fieldName) => {
        const nextPayload = { ...numericPayload };
        nextPayload[fieldName] = getNumericRegisterValue(registerValues?.[fieldName]);
        return nextPayload;
    }, {} as Record<string, any>);
    const registerDocuments = getRegisterUploadedDocuments(registerValues);
    const bankStatementUrls = getRegisterUploadedBankStatements(registerValues);

    return compactRegisterPayload({
        name: getTrimmedRegisterValue(registerValues?.name),
        mobile: getTrimmedRegisterValue(registerValues?.mobile),
        email: getTrimmedRegisterValue(registerValues?.email),
        referral: getTrimmedRegisterValue(registerValues?.referral),
        ktp_id: getTrimmedRegisterValue(registerValues?.ktp_id),
        npwp_no: getNormalizedNpwp(registerValues?.npwp_no),
        dob: getTrimmedRegisterValue(registerValues?.dob),
        housing: getTrimmedRegisterValue(registerValues?.housing),
        rt: getTrimmedRegisterValue(registerValues?.rt),
        rw: getTrimmedRegisterValue(registerValues?.rw),
        sub_district: getTrimmedRegisterValue(registerValues?.sub_district),
        branch_name: getTrimmedRegisterValue(registerValues?.branch_name),
        account_no: getTrimmedRegisterValue(registerValues?.account_no),
        beneficiary_name: getTrimmedRegisterValue(registerValues?.beneficiary_name),
        otp: getTrimmedRegisterValue(otp),
        ...numericFields,
        documents: registerDocuments,
        bank_statement_url: bankStatementUrls,
    });
};
